import { X, History } from 'lucide-react';

export default function WorklistDrawer({
    item,
    audit = [],
    slots = {},
    cols = [],
    statuses = [],
    assigneePool = [],
    onEdit,
    onRollback,
    onClose,
    isColEditable
}) {
    const resolved = item.resolved || {};
    const overrides = item.field_overrides || {};
    const colKeys = cols.map(c => c.key);
    const extraFields = Object.entries(resolved).filter(([k]) => !k.startsWith('__') && !colKeys.includes(k));

    return (
        <div className="fixed inset-y-0 right-0 z-50 w-[420px] bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-l border-gray-200 dark:border-slate-800/80 shadow-[0_0_50px_rgba(0,0,0,0.15)] flex flex-col transition-all duration-300 ease-out transform animate-slide-in">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-slate-800/80 flex-none bg-gray-50/50 dark:bg-slate-900/50">
                <div>
                    <h3 className="font-extrabold text-gray-900 dark:text-slate-100 truncate max-w-[280px] text-base leading-tight tracking-tight">{resolved[slots.title] || 'Item Detail'}</h3>
                    <p className="text-[10px] font-mono text-gray-400 dark:text-slate-500 mt-1 uppercase tracking-wider">Record ID: {item.id}</p>
                </div>
                <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all">
                    <X className="w-4 h-4" />
                </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-7 custom-scrollbar">
                {/* Interactive Edit Form */}
                <div className="space-y-4">
                    <p className="text-[10px] font-extrabold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">Interactive Fields</p>
                    {cols.map(col => {
                        const val = resolved[col.key];
                        const editable = isColEditable(col);

                        // Helper for rendering styled read-only content
                        const renderReadOnlyVal = (v, type) => {
                            if (v === undefined || v === null || v === '') {
                                return <span className="text-gray-300 dark:text-slate-600">—</span>;
                            }
                            const strVal = String(v);
                            if (type === 'currency') {
                                const num = parseFloat(v);
                                if (!isNaN(num)) {
                                    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
                                }
                            }
                            if (type === 'percentage') {
                                const num = parseFloat(v);
                                if (!isNaN(num)) {
                                    if (strVal.includes('%')) return strVal;
                                    return `${strVal}%`;
                                }
                            }
                            if (type === 'url') {
                                const href = strVal.startsWith('http') ? strVal : `https://${strVal}`;
                                return (
                                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-650 dark:text-indigo-400 hover:underline">
                                        {strVal}
                                    </a>
                                );
                            }
                            if (type === 'email') {
                                return (
                                    <a href={`mailto:${strVal}`} className="text-indigo-655 dark:text-indigo-400 hover:underline">
                                        {strVal}
                                    </a>
                                );
                            }
                            if (type === 'phone') {
                                return (
                                    <a href={`tel:${strVal}`} className="text-indigo-655 dark:text-indigo-400 hover:underline">
                                        {strVal}
                                    </a>
                                );
                            }
                            return strVal;
                        };

                        if (!editable) {
                            return (
                                <div key={col.key} className="space-y-1.5 bg-gray-50/10 dark:bg-slate-900/10 p-3.5 rounded-2xl border border-gray-100/50 dark:border-slate-800/40 opacity-80">
                                    <label className="block text-[10px] font-extrabold text-gray-400 dark:text-slate-500 uppercase tracking-wide flex items-center justify-between">
                                        <span>{col.label}</span>
                                        <span className="text-[9px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-1">🔒 Read-only</span>
                                    </label>
                                    <div className="text-xs font-semibold text-gray-700 dark:text-slate-350 px-1 py-0.5">
                                        {renderReadOnlyVal(val, col.type)}
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <div key={col.key} className="space-y-1.5 bg-gray-50/30 dark:bg-slate-900/30 p-3.5 rounded-2xl border border-gray-100/50 dark:border-slate-800/60 transition-all duration-300 hover:border-gray-200 dark:hover:border-slate-700">
                                <label className="block text-[10px] font-extrabold text-gray-400 dark:text-slate-500 uppercase tracking-wide flex items-center justify-between">
                                    <span>{col.label}</span>
                                    {overrides[col.key] !== undefined && (
                                        <button onClick={() => onRollback(item.id, col.key)}
                                            title="Rollback to original source value"
                                            className="text-[9px] font-bold text-amber-500 hover:text-amber-600 uppercase tracking-widest flex items-center gap-0.5 transition-colors">
                                            ↩ Rollback
                                        </button>
                                    )}
                                </label>
                                {col.key === slots.status ? (
                                    <select value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })}
                                        className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                                        {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                ) : col.key === slots.assignee ? (
                                    assigneePool.length > 0 ? (
                                        <select value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })}
                                            className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                                            <option value="">— Unassigned —</option>
                                            {assigneePool.map(a => <option key={a} value={a}>{a}</option>)}
                                        </select>
                                    ) : (
                                        <input className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                            value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })} />
                                    )
                                ) : col.type === 'dropdown' || col.type === 'multi' ? (
                                    <select value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })}
                                        className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all">
                                        <option value="">— Select —</option>
                                        {(col.options || '').split(',').map(o => o.trim()).filter(Boolean).map(o => (
                                            <option key={o} value={o}>{o}</option>
                                        ))}
                                    </select>
                                ) : col.type === 'textarea' ? (
                                    <textarea rows={3} className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all resize-none"
                                        value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })} />
                                ) : col.type === 'checkbox' ? (
                                    <div className="flex items-center gap-2 pt-1">
                                        <input type="checkbox" checked={!!val} onChange={e => onEdit(item.id, { [col.key]: e.target.checked })}
                                            className="rounded border-gray-300 text-indigo-650 focus:ring-indigo-500 h-4.5 w-4.5 bg-white dark:bg-slate-955" />
                                        <span className="text-xs font-medium text-gray-700 dark:text-slate-300">{val ? 'Checked' : 'Unchecked'}</span>
                                    </div>
                                ) : (col.type === 'date' || col.type === 'time' || col.type === 'datetime-local') ? (
                                    <input type={col.type} className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                        value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })} />
                                ) : (col.type === 'number' || col.type === 'currency' || col.type === 'percentage') ? (
                                    <input type="number" step="any" className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                        value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })} />
                                ) : (col.type === 'url' || col.type === 'email' || col.type === 'phone') ? (
                                    <input type={col.type === 'phone' ? 'tel' : col.type === 'url' ? 'url' : 'email'} className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                        value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })} />
                                ) : (
                                    <input className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-955 border border-gray-200 dark:border-slate-800 rounded-xl outline-none dark:text-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                        value={val || ''} onChange={e => onEdit(item.id, { [col.key]: e.target.value })} />
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Additional / System Data */}
                {extraFields.length > 0 && (
                    <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-slate-800">
                        <p className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">System / Source Data</p>
                        <div className="space-y-2 bg-gray-50/30 dark:bg-slate-900/50 p-3 rounded-xl border border-gray-100/50 dark:border-slate-800/40">
                            {extraFields.map(([k, v]) => (
                                <div key={k} className="flex flex-col gap-0.5 text-xs">
                                    <span className="font-semibold text-gray-400 dark:text-slate-500 uppercase text-[9px]">{k}</span>
                                    <span className="text-gray-700 dark:text-slate-300 break-all">{String(v ?? '—')}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Audit Trail */}
                {audit.length > 0 && (
                    <div className="pt-5 border-t border-gray-200 dark:border-slate-800/80">
                        <p className="text-[10px] font-extrabold text-gray-450 dark:text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                            <History className="w-3.5 h-3.5 text-indigo-500" /> System Audit Trail
                        </p>
                        <div className="relative pl-4 border-l-2 border-gray-200 dark:border-slate-800 space-y-4">
                            {audit.slice(0, 15).map((a) => (
                                <div key={a.id} className="relative group text-xs">
                                    {/* Timeline dot */}
                                    <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-indigo-500 ring-4 ring-white dark:ring-slate-900 group-hover:scale-125 transition-transform" />
                                    
                                    <div className="bg-gray-50/60 dark:bg-slate-800/50 rounded-2xl border border-gray-100/80 dark:border-slate-800/50 p-3.5 space-y-1.5 shadow-sm transition-all duration-300 hover:shadow-md hover:border-gray-200 dark:hover:border-slate-700">
                                        <div className="flex justify-between text-[10px] text-gray-400 dark:text-slate-500">
                                            <span className="font-extrabold uppercase text-indigo-600 dark:text-indigo-400 tracking-wider">{a.field_name}</span>
                                            <span>{new Date(a.changed_at).toLocaleDateString()}</span>
                                        </div>
                                        <div className="text-xs text-gray-600 dark:text-slate-300 leading-relaxed">
                                            {a.old_value ? (
                                                <span className="line-through text-gray-400">{a.old_value}</span>
                                            ) : (
                                                <span className="italic text-gray-350 dark:text-slate-650">None</span>
                                            )}
                                            <span className="mx-1 text-gray-400">→</span>
                                            <span className="font-bold text-gray-800 dark:text-slate-100 bg-gray-250 dark:bg-slate-800/80 px-1.5 py-0.5 rounded-md font-mono text-[11px]">{a.new_value}</span>
                                            
                                            {a.comment && (
                                                <div className="mt-2 p-2 bg-indigo-50/50 dark:bg-indigo-950/20 border-l-2 border-indigo-500 rounded-lg text-[10px] text-indigo-800 dark:text-indigo-300 italic font-medium leading-normal">
                                                    "{a.comment}"
                                                </div>
                                            )}
                                            
                                            {a.changed_by && (
                                                <span className="text-gray-400 dark:text-slate-500 text-[9px] block mt-1 uppercase font-bold tracking-wider">
                                                    by {a.changed_by}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
